import { useCallback } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  AdminClinic,
  ClinicsPageData,
  PortalTherapist,
  TherapistsPageData,
} from "../api/contracts";
import { usePortalData } from "./usePortalData";

export interface AdminDirectoryData {
  clinics: ClinicsPageData;
  therapists: TherapistsPageData;
}

function uniqueSortedNames(values: readonly string[]): string[] {
  const namesByKey = new Map<string, string>();

  for (const value of values) {
    const name = value.trim();
    const key = name.toLocaleLowerCase();
    if (name && !namesByKey.has(key)) namesByKey.set(key, name);
  }

  return [...namesByKey.values()].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" }),
  );
}

export function resolveTherapistNames(
  clinic: AdminClinic,
  therapists: readonly PortalTherapist[],
): string[] {
  const therapistNameById = new Map(
    therapists.map((therapist) => [therapist.id, therapist.therapistName]),
  );

  return uniqueSortedNames(
    clinic.therapistIds.flatMap((therapistId) => {
      const therapistName = therapistNameById.get(therapistId);
      return therapistName === undefined ? [] : [therapistName];
    }),
  );
}

export function resolveClinicNames(
  therapist: PortalTherapist,
  clinics: readonly AdminClinic[],
): string[] {
  return uniqueSortedNames(
    clinics.flatMap((clinic) =>
      clinic.therapistIds.includes(therapist.id) ? [clinic.clinicName] : [],
    ),
  );
}

export function useAdminDirectory(clinicianCode: string) {
  const request = useCallback(
    async (signal: AbortSignal): Promise<AdminDirectoryData> => {
      const [clinics, therapists] = await Promise.all([
        clinicianPortalApi.getClinics(clinicianCode, signal),
        clinicianPortalApi.getTherapists(clinicianCode, signal),
      ]);

      return { clinics, therapists };
    },
    [clinicianCode],
  );

  return usePortalData(request);
}
